import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { getFunnels, createFunnel, getFunnelById, type FunnelStep } from '../api/client';
import FunnelBuilder from '../components/funnels/FunnelBuilder';
import FunnelChart from '../components/funnels/FunnelChart';
import { formatDate } from '../utils/formatters';

export default function FunnelsPage() {
  const queryClient = useQueryClient();
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);

  const { data: funnels = [], isLoading } = useQuery({
    queryKey: ['funnels'],
    queryFn: getFunnels,
  });

  const { data: funnelResult } = useQuery({
    queryKey: ['funnel', selectedFunnelId],
    queryFn: () => getFunnelById(selectedFunnelId!),
    enabled: !!selectedFunnelId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; steps: FunnelStep[] }) => createFunnel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funnels'] });
      setShowBuilder(false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Funnels</h1>
        <button onClick={() => setShowBuilder(true)} className="btn-primary gap-2">
          <Plus className="h-4 w-4" />
          Create Funnel
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : funnels.length === 0 ? (
        <div className="card flex h-64 items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-500">No funnels yet</p>
            <p className="mt-1 text-sm text-gray-400">
              Create your first funnel to track conversion flows
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Funnel List */}
          <div className="space-y-3">
            {funnels.map((funnel) => (
              <button
                key={funnel.id}
                onClick={() => setSelectedFunnelId(funnel.id)}
                className={`card w-full text-left transition-shadow hover:shadow-md ${
                  selectedFunnelId === funnel.id ? 'ring-2 ring-brand-500' : ''
                }`}
              >
                <h3 className="text-sm font-semibold text-gray-900">{funnel.name}</h3>
                <p className="mt-1 text-xs text-gray-500">
                  {funnel.steps.length} steps &middot; Created {formatDate(funnel.createdAt)}
                </p>
              </button>
            ))}
          </div>

          {/* Funnel Visualization */}
          <div className="col-span-2">
            {funnelResult ? (
              <FunnelChart result={funnelResult} />
            ) : (
              <div className="card flex h-64 items-center justify-center">
                <p className="text-sm text-gray-500">Select a funnel to view results</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showBuilder && (
        <FunnelBuilder
          onClose={() => setShowBuilder(false)}
          onSave={(data) => createMutation.mutate(data)}
          isSaving={createMutation.isPending}
        />
      )}
    </div>
  );
}
